import { isValidElement, type ReactNode } from 'react'

import type { ExcludeString, Optional } from '../types/types'

export function hash(value: string) {
  let hash = 0,
    i,
    chr

  if (value.length === 0) {
    return hash
  }

  for (i = 0; i < value.length; i++) {
    chr = value.charCodeAt(i)
    hash = (hash << 5) - hash + chr
    hash |= 0 // Convert to 32bit integer
  }

  return hash
}

export function capitalizeFirstLetter(value: Optional<string>) {
  if (!value) {
    return value
  }

  return value.charAt(0).toUpperCase() + value.slice(1)
}

export const lowerCaseFirstLetter = (value: Optional<string>) => {
  if (!value) {
    return value
  }

  return value.charAt(0).toLowerCase() + value.slice(1)
}

export const addUpperCaseWordBreakOpportunity = (input: string) =>
  input.replace(/(?<![A-Z])([A-Z])/g, '\u200B$1')

// Escape the string for use in a regex pattern.
// The replace function adds a backslash before any regex special character so that the path is matched literally.
export const escapeRegexp = (string: string) =>
  string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

export const strictEqual = <
  T extends Optional<string>,
  A extends ExcludeString<T>,
>(
  originalValue: T,
  toCheck: ExcludeString<A>,
): originalValue is A => originalValue === toCheck

/**
 * Helper function to check that a backend `value`
 * is present in the fixed list of allowed `literals`.
 */
export const strictIncludes = <
  T extends Optional<string>,
  const A extends readonly ExcludeString<T>[] = readonly ExcludeString<T>[],
>(
  value: T,
  literals: A,
): value is A[number] => literals.includes(value as ExcludeString<T>)

export const makePaddingSpacesWider = (value: ReactNode) =>
  typeof value === 'string'
    ? value.replace(/^\s+|\s+$/g, (match) => '\u2003'.repeat(match.length)) // Make empty spaces more visible with em-space.
    : value

/**
 * Converts a ReactNode to a string representation for display and sorting purposes.
 */
export const reactNodeToDisplayText = (value: ReactNode): string => {
  if (typeof value === 'string') {
    return value
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return `${value}`
  }

  if (Array.isArray(value)) {
    return value
      .map((child) => reactNodeToDisplayText(child))
      .join(' ')
      .trim()
  }

  if (isValidElement<{ children?: ReactNode }>(value)) {
    return reactNodeToDisplayText(value.props.children)
  }

  return ''
}
